import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  auth
} from '../firebase';
import { writePortalUiFlags } from '../portal-oauth/uiFlags';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  // Convert Firebase errors to simple, funny messages
  const getQuirkyErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/invalid-credential': [
        "Wrong email or password! Double-check and try again",
        "Those credentials don't match our records",
        "Login failed! Make sure your email and password are correct",
        "Invalid credentials! Check your spelling and try once more",
        "Authentication failed! Please verify your login details"
      ],
      'auth/user-not-found': [
        "No account found! Did you sign up with a different email?",
        "User not found! That email isn't in our system",
        "Account missing! Check if you used the right email address",
        "Unknown user! Make sure you're using the correct email",
        "Email not recognized! Try the email you used to sign up"
      ],
      'auth/wrong-password': [
        "Wrong password! Give it another shot",
        "Password doesn't match! Try typing it again carefully",
        "Incorrect password! Double-check what you typed",
        "Password error! Make sure caps lock isn't on",
        "Wrong password! One more try should do it"
      ],
      'auth/too-many-requests': [
        "Too many attempts! Take a 5-minute break",
        "Going too fast! Slow down and try again later",
        "Security timeout! Wait a few minutes before trying again",
        "Too many tries! Our system needs a quick rest",
        "Rate limited! Give it a rest and come back soon"
      ]
    };

    // Get random message from the array for the error code
    const messages = errorMessages[errorCode];
    if (messages) {
      return messages[Math.floor(Math.random() * messages.length)];
    }

    // Default message for unknown errors
    const defaultMessages = [
      "Oops! Something unexpected happened. Try again!",
      "Unexpected error! Give it another go",
      "System hiccup! Try logging in once more",
      "Technical glitch! One more attempt should work",
      "Something went wrong! Please try again"
    ];
    return defaultMessages[Math.floor(Math.random() * defaultMessages.length)];
  };

  // Sign In Function (only sign in, no signup)
  const signIn = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      // Extract error code from Firebase error message
      const errorCode = error.code || 'unknown-error';
      const quirkyMessage = getQuirkyErrorMessage(errorCode);
      return { success: false, error: quirkyMessage };
    }
  };

  // Sign Out Function
  const logout = async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      const quirkyMessage = getQuirkyErrorMessage('unknown-error');
      return { success: false, error: quirkyMessage };
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Cross-tab sync: when another tab completes a portal OAuth login,
  // force a full reload so stale user data (company, trips, config) is cleared.
  useEffect(() => {
    function handleStorageChange(e) {
      if (e.key !== 'portal_oauth_login' || !e.newValue) return;
      // Flag so App.js shows the portal skeleton during the reload.
      try {
        sessionStorage.setItem('portal_oauth_reloading', '1');
        // Copy chrome flags into this tab's sessionStorage before reload.
        const payload = JSON.parse(e.newValue);
        const chrome = {};
        if (typeof payload?.isShowSidenav === 'boolean') {
          chrome.isShowSidenav = payload.isShowSidenav;
        }
        if (typeof payload?.isDisablePlatform === 'boolean') {
          chrome.isDisablePlatform = payload.isDisablePlatform;
        }
        if (Object.keys(chrome).length) {
          writePortalUiFlags(chrome);
        }
      } catch { /* ignore parse/storage errors */ }
      window.location.reload();
    }

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      signIn,
      logout,
      loading,
      sidebarOpen,
      setSidebarOpen,
      logoutModalOpen,
      setLogoutModalOpen
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);